'use client'

import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { format, parseISO } from 'date-fns'
import {
  HiArrowTrendingUp,
  HiCheck,
  HiChevronUp,
  HiOutlineBuildingOffice2,
  HiOutlineCalendarDays,
  HiOutlineClipboard,
  HiOutlineDocumentText,
  HiOutlineMapPin,
} from 'react-icons/hi2'
import { twMerge } from 'tailwind-merge'

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/common/Dialog'
import { RecipientLink } from '@/components/usaspending/RecipientLink'

import {
  CERTIFICATION_BADGES,
  type CertificationType,
  type RecipientSearchResult,
} from '@/types/recipient-search'

import { useRecipientFilterActions } from './RecipientFilterContext'

interface RecipientCardProps {
  recipient: RecipientSearchResult
  className?: string
}

/**
 * Format currency in compact notation
 */
function formatCurrency(amount: number | null): string {
  if (amount === null || amount === undefined) return '—'
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(amount)
}

/**
 * Format number with commas
 */
function formatNumber(num: number | null): string {
  if (num === null || num === undefined) return '—'
  return new Intl.NumberFormat('en-US').format(num)
}

/**
 * Get active certifications for a recipient
 */
function getActiveCertifications(
  recipient: RecipientSearchResult,
): CertificationType[] {
  const certs: CertificationType[] = []
  if (recipient.is_8a) certs.push('is_8a')
  if (recipient.hubzone) certs.push('hubzone')
  if (recipient.sdvosb) certs.push('sdvosb')
  if (recipient.woman_owned) certs.push('woman_owned')
  if (recipient.veteran_owned) certs.push('veteran_owned')
  if (recipient.sdb) certs.push('sdb')
  if (recipient.minority_owned) certs.push('minority_owned')
  if (recipient.small_business) certs.push('small_business')
  if (recipient.alaska_native) certs.push('alaska_native')
  if (recipient.native_american) certs.push('native_american')
  if (recipient.nonprofit) certs.push('nonprofit')
  if (recipient.educational) certs.push('educational')
  return certs
}

/**
 * Certification badge component
 */
const CertificationBadge: React.FC<{
  type: CertificationType
  onClick?: (type: CertificationType) => void
}> = memo(({ type, onClick }) => {
  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      if (onClick) {
        e.preventDefault()
        e.stopPropagation()
        onClick(type)
      }
    },
    [onClick, type],
  )

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (onClick && (e.key === 'Enter' || e.key === ' ')) {
        e.preventDefault()
        e.stopPropagation()
        onClick(type)
      }
    },
    [onClick, type],
  )

  const config = CERTIFICATION_BADGES.find((b) => b.type === type)
  if (!config) return null

  return (
    <span
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onClick={handleClick}
      onKeyDown={onClick ? handleKeyDown : undefined}
      className={twMerge(
        'inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold tracking-wide',
        config.color,
        config.darkColor,
        onClick &&
          'cursor-pointer hover:ring-1 hover:ring-current/50 transition-shadow',
      )}
    >
      {config.shortLabel}
    </span>
  )
})

CertificationBadge.displayName = 'CertificationBadge'

/**
 * Stat item for the metrics row
 */
const StatItem: React.FC<{
  label: string
  value: string
  subValue?: string
  highlight?: boolean
  colorClass?: string
}> = memo(({ label, value, subValue, highlight, colorClass }) => (
  <div className="flex flex-col">
    <span className="text-[10px] font-medium uppercase tracking-wider text-primary-400 dark:text-primary-500">
      {label}
    </span>
    <span
      className={twMerge(
        'text-sm font-semibold tabular-nums',
        highlight
          ? 'text-emerald-600 dark:text-emerald-400'
          : colorClass || 'text-primary-900 dark:text-primary-100',
      )}
    >
      {value}
    </span>
    {subValue && (
      <span className="text-[10px] text-primary-400 dark:text-primary-500 tabular-nums">
        {subValue}
      </span>
    )}
  </div>
))

StatItem.displayName = 'StatItem'

/**
 * NAICS code chip component (shared styling)
 */
const NaicsCodeChip = memo(function NaicsCodeChip({
  code,
  highlighted,
  onClick,
}: {
  code: string
  highlighted?: boolean
  onClick?: (code: string) => void
}) {
  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault()
      e.stopPropagation()
      onClick?.(code)
    },
    [onClick, code],
  )

  return (
    <button
      type="button"
      onClick={handleClick}
      className={twMerge(
        'px-1.5 py-0.5 text-[10px] font-mono rounded',
        highlighted
          ? 'border border-accent-200 bg-accent-50 text-accent-600 font-semibold dark:border-accent-700 dark:bg-accent-900/20 dark:text-accent-400'
          : 'bg-white dark:bg-primary-700 shadow-sm text-primary-600 dark:text-primary-300',
        onClick &&
          'cursor-pointer hover:ring-1 hover:ring-accent-500/50 transition-shadow',
      )}
    >
      {code}
    </button>
  )
})

/**
 * Desktop NAICS inline trigger (stays in metrics row)
 */
const NaicsInlineTrigger = memo(function NaicsInlineTrigger({
  codes,
  expanded,
  onToggle,
  onFilterByNaics,
  selectedNaicsCodes,
}: {
  codes: string[]
  expanded: boolean
  onToggle: (e: React.MouseEvent) => void
  onFilterByNaics?: (code: string) => void
  selectedNaicsCodes?: ReadonlySet<string>
}) {
  const hasMore = codes.length > 2

  return (
    <div className="flex items-center gap-1.5 px-2 py-1 bg-primary-50 dark:bg-primary-800/80 rounded-md">
      <span className="text-[10px] font-semibold uppercase tracking-wide text-primary-400 dark:text-primary-500">
        NAICS
      </span>
      <div className="flex items-center gap-1">
        {codes.slice(0, 2).map((code) => (
          <NaicsCodeChip
            key={code}
            code={code}
            highlighted={selectedNaicsCodes?.has(code)}
            onClick={onFilterByNaics}
          />
        ))}
        {hasMore && (
          <button
            type="button"
            onClick={onToggle}
            className="flex items-center justify-center gap-1 px-2.5 py-1 rounded bg-accent-50 dark:bg-accent-900/30 hover:bg-accent-100 dark:hover:bg-accent-800/40 transition-colors"
          >
            <span className="text-[10px] font-semibold text-accent-600 dark:text-accent-400">
              {expanded ? 'less' : `+${codes.length - 2} more`}
            </span>
            {expanded && (
              <HiChevronUp className="w-3.5 h-3.5 text-accent-500 dark:text-accent-400" />
            )}
          </button>
        )}
      </div>
    </div>
  )
})

/**
 * Desktop NAICS expanded panel (rendered below metrics row)
 */
const NaicsExpandedPanel = memo(function NaicsExpandedPanel({
  codes,
  expanded,
  onFilterByNaics,
  selectedNaicsCodes,
}: {
  codes: string[]
  expanded: boolean
  onFilterByNaics?: (code: string) => void
  selectedNaicsCodes?: ReadonlySet<string>
}) {
  return (
    <div
      className={twMerge(
        'grid transition-[grid-template-rows] duration-200 ease-out',
        expanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
      )}
    >
      <div className="overflow-hidden">
        <div className="mt-3 px-3 py-2 bg-primary-50/50 dark:bg-primary-800/30 rounded-md border border-primary-100 dark:border-primary-700">
          <span className="text-[10px] font-medium uppercase tracking-wide text-primary-400 dark:text-primary-500">
            NAICS Codes
          </span>
          <div className="flex flex-wrap gap-1.5 mt-1.5">
            {codes.map((code) => (
              <NaicsCodeChip
                key={code}
                code={code}
                highlighted={selectedNaicsCodes?.has(code)}
                onClick={onFilterByNaics}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  )
})

/**
 * Mobile NAICS bottom sheet component
 */
const NaicsMobileSheet = memo(function NaicsMobileSheet({
  codes,
  onFilterByNaics,
  selectedNaicsCodes,
}: {
  codes: string[]
  onFilterByNaics?: (code: string) => void
  selectedNaicsCodes?: ReadonlySet<string>
}) {
  const [open, setOpen] = useState(false)

  const handleTriggerClick = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setOpen(true)
  }, [])

  const handleCodeClick = useCallback(
    (code: string) => {
      onFilterByNaics?.(code)
      setOpen(false)
    },
    [onFilterByNaics],
  )

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <button
        type="button"
        onClick={handleTriggerClick}
        className="flex items-center gap-1 px-2 py-1 bg-primary-50 dark:bg-primary-800/80 rounded-md"
      >
        <span className="text-[10px] font-medium text-accent-600 dark:text-accent-400">
          {codes.length} NAICS
        </span>
      </button>
      <DialogContent
        className="fixed inset-x-0 bottom-0 w-full max-h-[70vh] p-0 rounded-t-2xl rounded-b-none data-[state=open]:slide-in-from-bottom data-[state=closed]:slide-out-to-bottom"
        overlayClassName="items-end"
        closeButtonClassName="top-3 right-3"
      >
        {/* Drag handle */}
        <div className="flex justify-center pt-2 pb-1">
          <div className="w-10 h-1 rounded-full bg-primary-200 dark:bg-primary-700" />
        </div>
        <DialogHeader className="px-4 pt-2 pb-3 border-b border-primary-100 dark:border-primary-800">
          <DialogTitle className="text-base">NAICS Codes</DialogTitle>
        </DialogHeader>
        <div className="p-4 pb-8 overflow-y-auto">
          <div className="grid grid-cols-2 gap-3">
            {codes.map((code) => (
              <button
                key={code}
                type="button"
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  handleCodeClick(code)
                }}
                className={twMerge(
                  'flex items-center justify-center min-h-[44px] px-4 py-3 text-sm font-mono font-medium rounded-lg transition-colors',
                  selectedNaicsCodes?.has(code)
                    ? 'border border-accent-200 bg-accent-50 text-accent-600 font-semibold dark:border-accent-700 dark:bg-accent-900/20 dark:text-accent-400'
                    : 'bg-primary-50 dark:bg-primary-800 text-primary-700 dark:text-primary-200 active:bg-accent-100 dark:active:bg-accent-900/40',
                )}
              >
                {code}
              </button>
            ))}
          </div>
          <p className="text-xs text-primary-400 dark:text-primary-500 mt-4 text-center">
            Tap a code to filter results
          </p>
        </div>
      </DialogContent>
    </Dialog>
  )
})

/**
 * Subtitle row showing location, subsidiary count (for parents), or parent name (for children).
 * Extracted as a separate component to avoid conditional branching in the main card
 * (per architecture-avoid-boolean-props) and enable memoization (per rerender-memo).
 */
const RecipientSubtitle = memo(function RecipientSubtitle({
  location,
  canFilterByState,
  onFilterByState,
  isParent,
  subsidiaryCount,
  parentName,
}: {
  location: string | null
  canFilterByState: boolean
  onFilterByState: (e: React.MouseEvent) => void
  isParent: boolean
  subsidiaryCount: number
  parentName: string | null
}) {
  // Determine the secondary detail to show after location
  const secondaryDetail = isParent
    ? subsidiaryCount > 0
      ? `${subsidiaryCount} ${subsidiaryCount === 1 ? 'subsidiary' : 'subsidiaries'}`
      : null
    : parentName

  return (
    <div className="flex items-center gap-2 mt-1 text-xs text-primary-500 dark:text-primary-400">
      {location ? (
        <button
          type="button"
          onClick={canFilterByState ? onFilterByState : undefined}
          className={twMerge(
            'flex items-center gap-1',
            canFilterByState &&
              'cursor-pointer hover:text-accent-600 dark:hover:text-accent-400 hover:underline transition-colors',
          )}
        >
          <HiOutlineMapPin className="w-3 h-3 flex-shrink-0" />
          {location}
        </button>
      ) : null}
      {secondaryDetail ? (
        <>
          {location ? (
            <span className="text-primary-300 dark:text-primary-600">|</span>
          ) : null}
          {isParent ? (
            <span className="text-blue-600 dark:text-blue-400 font-medium">
              {secondaryDetail}
            </span>
          ) : (
            <span className="flex items-center gap-1 truncate">
              <HiOutlineBuildingOffice2 className="w-3 h-3 flex-shrink-0" />
              <span className="truncate max-w-[180px]">{secondaryDetail}</span>
            </span>
          )}
        </>
      ) : null}
    </div>
  )
})

/**
 * Recipient card component for search results
 * Redesigned with data-dense, scannable layout
 */
const RecipientCard: React.FC<RecipientCardProps> = ({
  recipient,
  className,
}) => {
  const filterActions = useRecipientFilterActions()
  const [copiedUei, setCopiedUei] = useState(false)
  const [naicsExpanded, setNaicsExpanded] = useState(false)
  const copyTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (copyTimeoutRef.current) {
        clearTimeout(copyTimeoutRef.current)
      }
    }
  }, [])

  const certifications = useMemo(
    () => getActiveCertifications(recipient),
    [recipient],
  )

  // Determine activity status based on last award date
  const activityStatus = useMemo(() => {
    if (!recipient.last_award_date) return 'unknown'
    const lastAward = parseISO(recipient.last_award_date)
    const now = new Date()
    const monthsAgo =
      (now.getTime() - lastAward.getTime()) / (1000 * 60 * 60 * 24 * 30)
    if (monthsAgo <= 6) return 'active'
    if (monthsAgo <= 12) return 'recent'
    if (monthsAgo <= 24) return 'moderate'
    return 'inactive'
  }, [recipient.last_award_date])

  const handleShowSubsidiaries = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault()
      e.stopPropagation()
      filterActions?.showSubsidiaries(
        recipient.recipient_hash,
        recipient.name || 'Unknown',
      )
    },
    [filterActions, recipient.recipient_hash, recipient.name],
  )

  const handleCopyUei = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault()
      e.stopPropagation()
      if (recipient.uei) {
        navigator.clipboard.writeText(recipient.uei)
        setCopiedUei(true)
        if (copyTimeoutRef.current) {
          clearTimeout(copyTimeoutRef.current)
        }
        copyTimeoutRef.current = setTimeout(() => setCopiedUei(false), 2000)
      }
    },
    [recipient.uei],
  )

  const handleFilterByState = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault()
      e.stopPropagation()
      if (filterActions && recipient.state) {
        filterActions.filterByState(recipient.state)
      }
    },
    [filterActions, recipient.state],
  )

  const handleFilterByNaics = useCallback(
    (code: string) => {
      filterActions?.filterByNaics(code)
    },
    [filterActions],
  )

  const handleToggleNaics = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setNaicsExpanded((prev) => !prev)
  }, [])

  const location = useMemo(() => {
    const parts = []
    if (recipient.city) parts.push(recipient.city)
    if (recipient.state) parts.push(recipient.state)
    return parts.length > 0 ? parts.join(', ') : null
  }, [recipient.city, recipient.state])

  // Compute the breakdown string for contracts/grants
  const spendingBreakdown = useMemo(() => {
    const parts = []
    if (recipient.last_12_contracts) {
      parts.push(`${formatCurrency(recipient.last_12_contracts)} contracts`)
    }
    if (recipient.last_12_grants) {
      parts.push(`${formatCurrency(recipient.last_12_grants)} grants`)
    }
    return parts.join(' · ')
  }, [recipient.last_12_contracts, recipient.last_12_grants])

  // Activity status indicator colors
  const activityBorderColor = useMemo(() => {
    switch (activityStatus) {
      case 'active':
        return 'border-l-emerald-500 dark:border-l-emerald-400'
      case 'recent':
        return 'border-l-blue-500 dark:border-l-blue-400'
      case 'moderate':
        return 'border-l-amber-500 dark:border-l-amber-400'
      case 'inactive':
        return 'border-l-primary-300 dark:border-l-primary-600'
      default:
        return 'border-l-primary-200 dark:border-l-primary-700'
    }
  }, [activityStatus])

  const activityDateColor = useMemo(() => {
    switch (activityStatus) {
      case 'active':
        return 'text-emerald-600 dark:text-emerald-400'
      case 'recent':
        return 'text-blue-600 dark:text-blue-400'
      case 'moderate':
        return 'text-amber-600 dark:text-amber-400'
      default:
        return 'text-primary-500 dark:text-primary-400'
    }
  }, [activityStatus])

  const hasNaicsCodes =
    recipient.naics_codes && recipient.naics_codes.length > 0
  const canFilterByState = filterActions && recipient.state
  const awardCount =
    recipient.last_12_award_count ?? recipient.award_count ?? null
  const isParent = recipient.recipient_level === 'P'
  const isChild = recipient.recipient_level === 'C'

  return (
    <RecipientLink
      hash={recipient.recipient_id ?? recipient.recipient_hash ?? ''}
      level={recipient.recipient_level}
      className={twMerge(
        'group block w-full rounded-lg border border-l-[3px] bg-white dark:bg-primary-900 overflow-hidden transition-all duration-150',
        'border-primary-200 dark:border-primary-700',
        activityBorderColor,
        'hover:shadow-sm hover:shadow-primary-900/5 dark:hover:shadow-black/10',
        'focus:outline-none focus:ring-2 focus:ring-accent-500/40 focus:ring-offset-2 dark:focus:ring-offset-primary-900',
        className,
      )}
    >
      {/* Main Content Area */}
      <div className="p-4">
        {/* Top Row: Name + Primary Spending */}
        <div className="flex items-start justify-between gap-4 mb-3">
          {/* Left: Name, Location, Parent */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h3 className="font-semibold text-[15px] leading-tight text-primary-900 dark:text-primary-100 hover:text-accent-600 dark:hover:text-accent-400 hover:underline decoration-accent-600/30 dark:decoration-accent-400/30 transition-colors line-clamp-1">
                {recipient.name || 'Unknown Recipient'}
              </h3>
              {isParent && (
                <span className="flex-shrink-0 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold tracking-wide bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300">
                  <HiOutlineBuildingOffice2 className="w-3 h-3" />
                  Parent
                </span>
              )}
              {isChild && recipient.has_parent && (
                <span className="flex-shrink-0 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold tracking-wide bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300">
                  Child
                </span>
              )}
              {recipient.recipient_level === 'R' && (
                <span className="flex-shrink-0 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold tracking-wide bg-primary-100 text-primary-500 dark:bg-primary-800 dark:text-primary-400">
                  Standalone
                </span>
              )}
            </div>
            <RecipientSubtitle
              location={location}
              canFilterByState={!!canFilterByState}
              onFilterByState={handleFilterByState}
              isParent={isParent}
              subsidiaryCount={recipient.subsidiary_count}
              parentName={recipient.has_parent ? recipient.parent_name : null}
            />
          </div>

          {/* Right: Primary Spending Figure */}
          <div className="flex-shrink-0 text-right">
            <div
              className={twMerge(
                'text-xl font-bold tabular-nums tracking-tight',
                recipient.last_12_months
                  ? 'text-emerald-600 dark:text-emerald-400'
                  : 'text-primary-300 dark:text-primary-600',
              )}
            >
              {formatCurrency(recipient.last_12_months)}
            </div>
            <div className="text-[10px] font-medium uppercase tracking-wider text-primary-400 dark:text-primary-500">
              {isParent ? 'Last 12 Mo (all subs)' : 'Last 12 Mo'}
            </div>
          </div>
        </div>

        {/* Certifications Row */}
        {certifications.length > 0 && (
          <div className="flex flex-wrap items-center gap-1 mb-3">
            {certifications.map((cert) => (
              <CertificationBadge
                key={cert}
                type={cert}
                onClick={filterActions?.filterByCertification}
              />
            ))}
          </div>
        )}

        {/* Metrics Row */}
        <div className="flex items-end gap-3 sm:gap-6 pt-3 border-t border-primary-100 dark:border-primary-800">
          {/* Awards Count */}
          <div className="flex items-center gap-2">
            <div className="hidden sm:flex items-center justify-center w-7 h-7 rounded-md bg-primary-50 dark:bg-primary-800">
              <HiOutlineDocumentText className="w-4 h-4 text-primary-400 dark:text-primary-500" />
            </div>
            <StatItem
              label={isParent ? 'Awards (12 Mo, all)' : 'Awards (12 Mo)'}
              value={formatNumber(awardCount)}
            />
          </div>

          {/* Last Award Date */}
          <div className="flex items-center gap-2">
            <div className="hidden sm:flex items-center justify-center w-7 h-7 rounded-md bg-primary-50 dark:bg-primary-800">
              <HiOutlineCalendarDays className="w-4 h-4 text-primary-400 dark:text-primary-500" />
            </div>
            <StatItem
              label="Last Award"
              value={
                recipient.last_award_date
                  ? format(parseISO(recipient.last_award_date), 'MMM yyyy')
                  : '—'
              }
              colorClass={activityDateColor}
            />
          </div>

          {/* Total All-Time (if significant) */}
          {recipient.total_amount != null &&
          recipient.total_amount !== 0 &&
          recipient.total_amount !== recipient.last_12_months ? (
            <div className="flex items-center gap-2">
              <div className="hidden sm:flex items-center justify-center w-7 h-7 rounded-md bg-primary-50 dark:bg-primary-800">
                <HiArrowTrendingUp className="w-4 h-4 text-primary-400 dark:text-primary-500" />
              </div>
              <StatItem
                label="All Time"
                value={formatCurrency(recipient.total_amount)}
              />
            </div>
          ) : null}

          {/* Spacer + NAICS/Subsidiary Actions */}
          <div className="flex-1" />

          <div className="flex items-center gap-3">
            {/* NAICS Codes - Desktop: Inline Trigger */}
            {hasNaicsCodes && (
              <div className="hidden sm:block">
                <NaicsInlineTrigger
                  codes={recipient.naics_codes!}
                  expanded={naicsExpanded}
                  onToggle={handleToggleNaics}
                  onFilterByNaics={handleFilterByNaics}
                  selectedNaicsCodes={filterActions?.selectedNaicsCodes}
                />
              </div>
            )}

            {/* NAICS Codes - Mobile: Bottom Sheet */}
            {hasNaicsCodes && (
              <div className="sm:hidden">
                <NaicsMobileSheet
                  codes={recipient.naics_codes!}
                  onFilterByNaics={handleFilterByNaics}
                  selectedNaicsCodes={filterActions?.selectedNaicsCodes}
                />
              </div>
            )}

            {/* Subsidiaries Button — prominent for parents, subtle otherwise */}
            {recipient.subsidiary_count > 0 && filterActions && (
              <button
                type="button"
                onClick={handleShowSubsidiaries}
                className={twMerge(
                  'flex items-center gap-1 px-2 py-1 text-[11px] font-medium rounded transition-colors',
                  isParent
                    ? 'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30 hover:bg-blue-100 dark:hover:bg-blue-800/40'
                    : 'text-primary-500 dark:text-primary-400 bg-primary-50 dark:bg-primary-800 hover:bg-primary-100 dark:hover:bg-primary-700 hover:text-primary-700 dark:hover:text-primary-200',
                )}
              >
                <HiOutlineBuildingOffice2 className="w-3.5 h-3.5" />
                {isParent ? 'View' : ''} {recipient.subsidiary_count}{' '}
                {recipient.subsidiary_count === 1 ? 'Sub' : 'Subs'}
              </button>
            )}
          </div>
        </div>

        {/* NAICS Codes - Desktop: Expanded Panel (below metrics row) */}
        {hasNaicsCodes && recipient.naics_codes!.length > 2 && (
          <div className="hidden sm:block">
            <NaicsExpandedPanel
              codes={recipient.naics_codes!}
              expanded={naicsExpanded}
              onFilterByNaics={handleFilterByNaics}
              selectedNaicsCodes={filterActions?.selectedNaicsCodes}
            />
          </div>
        )}

        {/* Secondary Info Row: Spending Breakdown + UEI */}
        {(spendingBreakdown || recipient.uei) && (
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 sm:gap-2 mt-2 pt-2 border-t border-primary-100 dark:border-primary-800">
            {spendingBreakdown && (
              <span className="text-[10px] text-primary-500 dark:text-primary-400">
                {spendingBreakdown}
              </span>
            )}
            {recipient.uei && (
              <button
                type="button"
                onClick={handleCopyUei}
                className="group/uei flex items-center gap-1.5 text-[10px] cursor-pointer"
              >
                <span className="font-semibold uppercase tracking-wide text-primary-400 dark:text-primary-500 group-hover/uei:text-accent-600 dark:group-hover/uei:text-accent-400 transition-colors">
                  UEI
                </span>
                <span className="font-mono text-primary-500 dark:text-primary-400 tracking-wide group-hover/uei:text-accent-600 dark:group-hover/uei:text-accent-400 transition-colors">
                  {recipient.uei}
                </span>
                {copiedUei ? (
                  <HiCheck className="w-3 h-3 text-emerald-500" />
                ) : (
                  <HiOutlineClipboard className="w-3 h-3 opacity-0 group-hover/uei:opacity-100 transition-opacity" />
                )}
              </button>
            )}
          </div>
        )}
      </div>
    </RecipientLink>
  )
}

export default memo(RecipientCard, (prev, next) => {
  return prev.recipient.recipient_id === next.recipient.recipient_id
})
